// src/components/RequestDemo/RequestDemo.tsx
import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { HiOutlineRefresh, HiCheckCircle } from 'react-icons/hi';
import { FaShieldAlt } from 'react-icons/fa';
import { ArrowLeft, X } from 'lucide-react';
import { trackPageView, trackRequestDemoEvent } from '../../utlis/useAnalytics';
import {PageName} from '../../configs/config'
import { Helmet } from "react-helmet-async";


// Import images
import DBS from './assets/dbs.webp';
import CC from './assets/CC-1.webp';
import Yesbank from './assets/YESBANK.NS_BIG.webp';
import Visa from './assets/VISA-1.webp';

const RequestDemo = () => {
  useEffect(() => {
    trackPageView(PageName.RequestDemo);
  }, []);


  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const features = [
    { id: 1, text: 'Full-fledged cross-border payments platform with best FX rates' },
    { id: 2, text: 'Access to global trade and collection accounts powered by AD1 Banks, fully compliant with RBI regulations' },
    { id: 3, text: 'Access to reliable trade finance at the best market rates' },
    { id: 4, text: 'Streamlined international transactions with reduced paperwork' },
  ];
  const brandLogos = [
    { src: DBS, alt: 'DBS Bank' },
    { src: CC, alt: 'Credit Suisse' },
    { src: Yesbank, alt: 'Yes Bank' },
    { src: Visa, alt: 'Visa' },
  ];

  // Simple front-end validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    if (!companyName.trim()) newErrors.companyName = 'Company name is required';

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (phoneNumber.length !== 10 || isNaN(Number(phoneNumber))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      setErrors({ ...errors, recaptcha: 'Please complete the reCAPTCHA' });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      phone_number: `+91${phoneNumber}`,
      email,
    };

    try {
      const response = await fetch('https://api.eximpe.com/service/lead/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        trackRequestDemoEvent({
          page: 'Request Demo',
          companyName,
          email,
        });

        setSubmitted(true);
        // reset form fields
        setFirstName('');
        setLastName('');
        setCompanyName('');
        setPhoneNumber('');
        setEmail('');
        recaptchaRef.current?.reset();
      } else {
        setErrors({ form: 'An error occurred. Please try again.' });
      }
    } catch (err) {
      console.error(err);
      setErrors({ form: 'An error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
     <Helmet>
        <title>Request a Free Demo – Experience EximPe in Action</title>
        <meta name="description" content="Book a free demo with EximPe. See how our digital trade solutions streamline your export-import operations and boost your business efficiency." />
      </Helmet>
      {/* Main Section */}
      <div className="text-[#1A202C] bg-white mt-20 min-h-screen overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              Request a Demo
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience our powerful cross-border payment platform and see how we can help your business grow
            </p>
          </div>

          <div className="flex flex-col lg:flex-row lg:space-x-12">
            {/* Features & Logos */}
            <div className="lg:w-1/2 space-y-8 order-2 lg:order-1 mt-12 lg:mt-0">
              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="text-xl text-center font-semibold text-gray-900 mb-6">
                  Why Choose Our Platform
                </h2>
                <div className="space-y-5">
                  {features.map(f => (
                    <div key={f.id} className="flex items-start group">
                      <HiCheckCircle className="flex-shrink-0 mt-1 h-5 w-5 text-[#27a887] group-hover:text-teal-600 transition-colors" />
                      <p className="ml-3 text-base text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                        {f.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-2xl border md:mx-[30%] lg:mx-0 border-teal-100">
                <div className="flex items-center justify-center mb-5">
                  <FaShieldAlt className="h-5 w-5 text-teal-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Powered By</h3>
                </div>
                <div className="flex justify-center">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 justify-center items-center">
                    {brandLogos.map(logo => (
                      <div
                        key={logo.alt}
                        className="w-20 h-20 p-2 flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all"
                      >
                        <img src={logo.src} alt={logo.alt} className="max-w-full max-h-full object-contain" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:w-1/2 flex justify-center order-1 lg:order-2">
              <div className="w-full max-w-md">
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter your details</h2>

                  {errors.form && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                      {errors.form}
                    </div>
                  )}

                  <form className="space-y-5" onSubmit={handleSubmit}>
                    {/* First Name */}
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        placeholder="Enter your first name"
                        className={`w-full p-3 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all`}
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        required
                      />
                      {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        placeholder="Enter your last name"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </div>

                    {/* Company Name */}
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        id="companyName"
                        type="text"
                        placeholder="Enter your company name"
                        className={`w-full p-3 border ${errors.companyName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all`}
                        value={companyName}
                        onChange={e => setCompanyName(e.target.value)}
                        required
                      />
                      {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <div className="flex">
                        <div className="bg-gray-100 flex items-center justify-center px-3 rounded-l-lg border border-gray-300">
                          <span className="text-gray-500">+91</span>
                        </div>
                        <input
                          id="phoneNumber"
                          type="tel"
                          placeholder="10 digit phone number"
                          className={`w-full p-3 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-r-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all`}
                          value={phoneNumber}
                          onChange={e => setPhoneNumber(e.target.value)}
                          required
                        />
                      </div>
                      {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        className={`w-full p-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all`}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>

                    {/* reCAPTCHA */}
                    <div>
                      <ReCAPTCHA sitekey="6LfFOAYqAAAAAP7ZFBmgFfROQ1xxEy-UBFkSjv00" ref={recaptchaRef} />
                      {errors.recaptcha && <p className="mt-1 text-sm text-red-600">{errors.recaptcha}</p>}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-3 px-4 text-white font-semibold bg-[#27a887] hover:bg-teal-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <HiOutlineRefresh className="animate-spin mr-2" /> Processing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <HiCheckCircle className="w-5 h-5 mr-2" /> Request Your Demo
                        </span>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {submitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-xl relative">
            {/* Close Button */}
            <button
              aria-label="Close"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              onClick={() => setSubmitted(false)}
            >
              <X className="h-6 w-6" />
            </button>

            <div className="flex flex-col items-center space-y-6 text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center">
                <HiCheckCircle className="w-10 h-10 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Thank You!</h2>
              <p className="text-gray-600">
                We've received your request and our team will get in touch with you shortly.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-teal-600 mt-4 flex items-center space-x-2 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-700 transition duration-300"
              >
                <ArrowLeft className="h-5 w-5" /> <span>Go Home</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestDemo;
